import { useApps } from '@auth/useApps'
import { Account, AccountTypeEnum, getDelegatorPoints } from '@kudos/http-client'
import { AccountPermission, AppPermission, hasPermission } from '@shared/permissions'
import { useQuery } from '@tanstack/react-query'

export type UseCanSendPointsOptions = {
  delegatorId?: string
}

export type UseCanSendPointsResult = {
  canSendPoints: boolean
  delegatorAccounts: Account[] | undefined
  isDelegatorPointsFetched: boolean
}

export function useCanSendPoints(options: UseCanSendPointsOptions = {}): UseCanSendPointsResult {
  const { delegatorId } = options
  const [hasAppPermission] = useApps()

  const { data: delegatorAccounts, isFetched: isDelegatorPointsFetched } = useQuery({
    queryKey: ['delegatorPoints', delegatorId],
    queryFn: () => getDelegatorPoints(delegatorId ?? ''),
    enabled: !!delegatorId,
  })

  const delegatorAllocationAccount = delegatorAccounts?.find(
    account => account.type === AccountTypeEnum.ALLOCATION,
  )

  const canSendDelegatedPoints =
    delegatorAllocationAccount &&
    hasPermission(delegatorAllocationAccount, AccountPermission.SendDelegatedPoints)

  // pointsApp:sendPoints reflects the requesting user's own point configuration, so it must not be
  // used when delegating — the delegator's configuration is what matters. pointsApp:viewPointsConfiguration
  // is user-agnostic and is the canonical signal that points are disabled tenant-wide.
  const canSendPoints = delegatorId
    ? Boolean(canSendDelegatedPoints)
    : hasAppPermission('pointsApp', AppPermission.PointsAppSendPoints)

  return {
    canSendPoints,
    delegatorAccounts,
    isDelegatorPointsFetched,
  }
}

export default useCanSendPoints
